import { DataEvents, Diagram, DiagramEvents, SelectionEvents, locale } from "@dhx/ts-diagram";
import { downloadFile, extend, uid } from "@dhx/ts-common/core";
import { Layout } from "@dhx/ts-layout";
import { Id } from "@dhx/ts-common/types";
import { ContextMenu } from "@dhx/ts-menu";
import { IDataItem } from "@dhx/ts-data";

import { BaseDiagramEditor } from "./BaseDiagramEditor";
import { Sidebar } from "./Sidebar";
import { BlockSelection } from "./BlockSelection";
import { Controls } from "./controls/Controls";
import { topbar } from "./helpers/topbar";
import { DiagramEditorEvents, IMindmapEditorConfig, IOrgEditorConfig } from "./types";

export class OrgChartEditor extends BaseDiagramEditor {
	public config: IOrgEditorConfig | IMindmapEditorConfig;
	private _menu: ContextMenu;

	constructor(container: string | HTMLElement, config: IOrgEditorConfig | IMindmapEditorConfig) {
		const cfg = extend(
			{
				itemsDraggable: true,
			},
			config
		);
		super(container, cfg);

		this._menu = new ContextMenu(null);
		this._menu.events.on("click", action => {
			const selectionItem = this.diagram.selection.getItem();
			if (
				!this.events.fire(DiagramEditorEvents.beforeShapeIconClick, [
					action as string,
					this.diagram.data.getItem(selectionItem.id),
				])
			) {
				return;
			}

			const item = { id: uid(), parent: selectionItem.id, dx: selectionItem.dx, dy: selectionItem.dy };

			switch (action) {
				case "vertical":
				case "horizontal":
					this.diagram.data.update(selectionItem.id, { dir: action });
					break;
				case "addAssistant":
					this.diagram.data.add({
						...item,
						assistant: true,
					});
					this.diagram.selection.add(item.id);
					this.diagram.showItem(item.id);
					break;
				case "addPartner":
					this.diagram.data.add({
						...item,
						partner: true,
					});
					this.diagram.selection.add(item.id);
					this.diagram.showItem(item.id);
					break;
				case "remove":
					this._removeItem(selectionItem.id);
					break;
			}

			this.events.fire(DiagramEditorEvents.afterShapeIconClick, [
				action as string,
				this.diagram.data.getItem(selectionItem.id),
			]);
		});
	}

	protected _setHandlers() {
		super._setHandlers();
		this.diagram.events.on(DiagramEvents.shapeIconClick, (action, event: MouseEvent) => {
			const selectionItem = this.diagram.selection.getItem();
			if (
				!this.events.fire(DiagramEditorEvents.beforeShapeIconClick, [
					action,
					this.diagram.data.getItem(selectionItem.id),
				])
			) {
				return;
			}
			const item = { id: uid(), parent: selectionItem.id, dx: selectionItem.dx, dy: selectionItem.dy };
			switch (action) {
				case "add":
					this.diagram.data.add(item);
					this.diagram.selection.add(item.id);
					this.diagram.showItem(item.id);
					break;
				case "addLeft":
					this.diagram.data.add({
						dir: "verticalLeft",
						...item,
					});
					this.diagram.selection.add(item.id);
					this.diagram.showItem(item.id, "left");
					break;
				case "addRight":
					this.diagram.data.add({
						dir: "verticalRight",
						...item,
					});
					this.diagram.selection.add(item.id);
					this.diagram.showItem(item.id, "right");
					break;
				case "remove":
					this._removeItem(selectionItem.id);
					break;
				case "menu":
					this._menu.data.parse(this._getContextMenuData(selectionItem));
					this._menu.showAt(event);
					break;
			}
			this.events.fire(DiagramEditorEvents.afterShapeIconClick, [
				action,
				this.diagram.data.getItem(selectionItem.id),
			]);
		});
		this.events.on(DiagramEditorEvents.exportData, () => {
			const data = JSON.stringify(this.serialize());
			downloadFile(data, "data.json", "text/json");
		});
		this.events.on(DiagramEditorEvents.changeGridStep, step => {
			this.diagram.config.gridStep = step;
			this.config.gridStep = step;
			this.paint();
		});
		this.events.on(DiagramEditorEvents.beforeShapeMove, () => {
			const shape = this.diagram.data.getItem(this.diagram.selection.getId());
			if (shape.$item.isFixed()) {
				return false;
			}
			if (!this._blockSelection.getSelected().length && shape.$connection) {
				this._blockSelection.add(shape.id);

				const getKids = parent => {
					parent.$connection.forEach(item => {
						this._blockSelection.add(item[1]);
						const kid = this.diagram.data.getItem(item[1]);
						if (kid?.$connection) getKids(kid);
					});
				};

				getKids(shape);
			}
		});
		this.events.on(DiagramEditorEvents.afterShapeMove, (event: MouseEvent, id: Id) => {
			if (!this.config.itemsDraggable) {
				return;
			}
			const movedItem = this.diagram.data.getItem(id);
			if (movedItem.giveItem === false) {
				return;
			}
			let findItem = false;
			this.diagram.data.mapVisible(targetItem => {
				if (!targetItem) return;

				const isLocate = targetItem?.$item.isLocate({
					width: movedItem.width,
					height: movedItem.height,
					x: movedItem.x + (movedItem.dx || 0),
					y: movedItem.y + (movedItem.dy || 0),
				});

				if (
					movedItem.id != targetItem.id &&
					movedItem.parent != targetItem.id &&
					targetItem.$item.getBaseType() === "shape"
				) {
					if (isLocate && !findItem) {
						findItem = true;
						targetItem.$target = true;

						this.events.fire(DiagramEditorEvents.itemTarget, [
							movedItem.id,
							targetItem.id,
							event,
						]);
					} else {
						targetItem.$target = false;
					}
				}
			});
		});
		this.events.on(DiagramEditorEvents.shapeMoveEnd, (event: MouseEvent, id: Id) => {
			if (!this.config.itemsDraggable) {
				return;
			}
			const movedItem = this.diagram.data.getItem(id);
			if (movedItem.giveItem === false) {
				return;
			}
			let findItem = false;
			this.diagram.data.mapVisible(targetItem => {
				if (!targetItem) return;

				targetItem.$target = false;

				const isLocate = targetItem.$item.isLocate({
					width: movedItem.width,
					height: movedItem.height,
					x: movedItem.x + (movedItem.dx || 0),
					y: movedItem.y + (movedItem.dy || 0),
				});

				if (
					movedItem.id != targetItem.id &&
					movedItem.parent != targetItem.id &&
					targetItem.$item.getBaseType() === "shape" &&
					!targetItem.assistant &&
					!targetItem.partner &&
					!findItem &&
					isLocate
				) {
					findItem = true;
					if (
						targetItem.catchItem === false ||
						!this.events.fire(DiagramEditorEvents.beforeItemCatch, [
							movedItem.id,
							targetItem.id,
							event,
						])
					) {
						return;
					}
					this.diagram.data.eachChild(
						movedItem.id,
						child => {
							child.dx = targetItem.dx;
							child.dy = targetItem.dy;
							return child;
						},
						true
					);
					this.diagram.data.update(
						movedItem.id,
						{ parent: targetItem.id, dx: targetItem.dx, dy: targetItem.dy },
						true
					);
					this.diagram.data.remove(movedItem.$parent);
					this.diagram.data.add({
						type: "line",
						from: targetItem.id,
						to: movedItem.id,
					});
					this.diagram.expandItem(targetItem.id);

					this.events.fire(DiagramEditorEvents.afterItemCatch, [
						movedItem.id,
						targetItem.id,
						event,
					]);
				}
			});
		});
		this.diagram.events.on(DataEvents.change, () => this._layout.paint());
		this.config.controls.autoLayout = false;
	}
	protected _initDiagram() {
		this.diagram = new Diagram(null, {
			type: this.config.type,
			select: true,
			toolbar: this._getToolbarConfig(this.config),
			defaultShapeType: this.config.shapeType,
			scroll: true,
			gridStep: this.config.gridStep,
			margin: {
				y: 70,
				x: this.config.reservedWidth,
			},
			lineGap: this.config.lineGap,
			defaults: this._getDefaults(),
			scale: this.config.scale,
		});
	}
	protected _initUI(container) {
		this._layout = new Layout(container, {
			height: "100%",
			width: "100%",
			css: "dhx_diagram_editor_org-chart",
			rows: [
				{
					id: "top",
					css: "dhx_topbar",
				},
				{
					css: "dhx_diagram_editor",
					cols: [
						{ id: "center", css: "dhx_diagram_editor__main" },
						{ id: "right", css: "dhx_diagram_editor__sidebar" },
					],
				},
			],
		});

		this._sidebar = new Sidebar(
			this.diagram,
			{
				showGridStep: this.config.controls.gridStep,
			},
			this.events
		);
		this._blockSelection = new BlockSelection(this.diagram);
		this._resizer = new Controls(this.events, this.diagram, this._blockSelection);

		this.diagram.config.$svg = size => {
			const id = this.diagram.selection.getId();
			const item = this.diagram.data.getItem(id);

			if (this._selectionBox && this._selectionBox.isValid()) {
				return this._selectionBox.render();
			}
			return this._blockSelection.getSelected().length ? null : this._resizer.render(item, size);
		};
		this.diagram.events.on(SelectionEvents.beforeSelect, id => {
			const item = this.diagram.data.getItem(id);
			return item.$item.getBaseType() === "shape";
		});

		this.diagram.data.parse([
			{
				id: uid(),
				x: 0,
				y: 0,
				text: "main",
			},
		]);

		this.diagram.events.on(DataEvents.load, () => {
			this.history.reset();
		});

		this._layout.getCell("top").attach(topbar, this);
		this._layout.getCell("right").attach(this._sidebar.getUI());
		this._layout.getCell("center").attach(this.diagram);
	}
	protected _initHotkeys() {
		super._initHotkeys();
		this._keyManager.addHotKey("ctrl+a", (e: KeyboardEvent) => {
			if (!this._sidebar.isItemsSelected()) {
				e.preventDefault();
				this.diagram.data.forEach(shape => {
					if (shape.$item.getBaseType() === "shape") {
						this.diagram.data.update(shape.id, { $blockSelected: "add" || "create" }, true);
					}
				});
				this.paint();
			}
		});
	}
	protected _getContextMenuData(item: IDataItem): IDataItem[] {
		const data: IDataItem[] = [
			{
				id: "addPartner",
				value: locale.menuAddPartner,
				icon: "dxi dxi-plus",
			},
			{
				id: "addAssistant",
				value: locale.menuAddAssistant,
				icon: "dxi dxi-plus",
			},
		];

		if (item.$connection && item.$connection.length) {
			if (item.dir !== "vertical" && !item?.$partners?.common?.length) {
				data.push({
					id: "vertical",
					value: locale.menuAlignChildrenVertically,
					icon: "dei dei-vertical",
				});
			} else if (item.dir === "vertical") {
				data.push({
					id: "horizontal",
					value: locale.menuAlignChildrenHorizontally,
					icon: "dei dei-horizontal",
				});
			}
		}
		if (item.$parent) {
			data.push(
				{
					type: "separator",
				},
				{
					id: "remove",
					value: locale.menuDelete,
					icon: "dxi dxi-delete-forever",
					hotkey: "Del",
				}
			);
		}

		return data;
	}
	protected _getDefaults() {
		let defaults = {
			text: {
				text: "Text",
			},
		};
		["card", "img-card", "topic"].forEach(type => {
			defaults[type] = {
				text: "Text",
				title: "Title",
				height: type !== "topic" ? 90 : 30,
				width: type === "img-card" ? 210 : 140,
			};
		});
		defaults = { ...defaults, ...this.config.defaults };

		return defaults;
	}
}
